import React, { useState } from 'react';
import { Modal, Button, Table, Typography, Tag, Space, message } from 'antd';
import { ShoppingCartOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import customerOrderService from '../services/customerOrderService.ts';
import SupplierAssociationModal from './SupplierAssociationModal.tsx';
import { formatCurrency } from '../utils/numberFormatting.ts';

const { Title, Text } = Typography;

interface ConvertToSupplierOrdersModalProps {
  visible: boolean;
  onClose: () => void;
  customerOrderId: number;
  customerOrderNumber: string;
}

interface SupplierOrderResult {
  supplier_order_id: number;
  supplier_name: string;
  order_number: string;
  estimated_delivery: string;
  products_count: number;
  products: Array<{
    product_code: string;
    product_description: string;
    quantity_ordered: number;
    shortage_quantity: number;
    cost: number;
  }>;
}

interface ConversionResult {
  message: string;
  customer_order_id: number;
  customer_order_number: string;
  supplier_orders: SupplierOrderResult[];
  error_type?: string;
  products_without_suppliers?: ProductWithoutSupplier[];
}

interface ProductWithoutSupplier {
  product_id: number;
  product_code: string;
  product_description: string;
  requested_quantity: number;
  shortage: number;
  available_stock: number;
  reserved_stock: number;
}

interface SupplierAssociation {
  product_id: number;
  supplier_id: number;
  cost?: number;
  lead_time_days?: number;
  minimum_order_quantity?: number;
  is_primary: boolean;
}

const ConvertToSupplierOrdersModal: React.FC<ConvertToSupplierOrdersModalProps> = ({
  visible,
  onClose,
  customerOrderId,
  customerOrderNumber,
}) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ConversionResult | null>(null);
  const [step, setStep] = useState<'confirm' | 'processing' | 'supplier-association' | 'result'>('confirm');
  const [showSupplierModal, setShowSupplierModal] = useState(false);

  const handleConvert = async () => {
    setLoading(true);
    setStep('processing');
    
    try {
      const conversionResult = await customerOrderService.convertToSupplierOrders(customerOrderId);
      
      // Controlla se la risposta indica prodotti senza fornitori
      if (conversionResult.error_type === 'missing_suppliers' && conversionResult.products_without_suppliers) {
        setResult(conversionResult);
        setStep('supplier-association');
        setShowSupplierModal(true);
        message.warning('Alcuni prodotti necessitano di associazioni fornitori');
      } else {
        setResult(conversionResult);
        setStep('result');
        message.success('Conversione completata con successo!');
      }
    } catch (error: any) {
      message.error('Errore durante la conversione: ' + (error.response?.data?.detail || error.message));
      setStep('confirm');
    } finally {
      setLoading(false);
    }
  };

  const handleAssociationsComplete = async (associations: SupplierAssociation[]) => {
    setLoading(true);
    setShowSupplierModal(false);
    setStep('processing');
    
    try {
      const associationResult = await customerOrderService.associateSuppliersAndConvert(
        customerOrderId, 
        associations
      );
      
      // Estrai il risultato della conversione
      const conversionResult = associationResult.conversion_result;
      setResult(conversionResult);
      setStep('result');
      message.success(`Associazioni create e conversione completata! (${associationResult.associations_created} associazioni)`);
      
    } catch (error: any) {
      message.error('Errore durante l\'associazione: ' + (error.response?.data?.detail || error.message));
      setStep('supplier-association');
      setShowSupplierModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSupplierModalClose = () => {
    setShowSupplierModal(false);
    setStep('confirm');
    setResult(null);
  };

  const handleClose = () => {
    setStep('confirm');
    setResult(null);
    setShowSupplierModal(false);
    onClose();
  };

  const productColumns = [
    {
      title: 'Codice Prodotto',
      dataIndex: 'product_code',
      key: 'product_code',
      render: (code: string) => <Text code>{code}</Text>,
    },
    {
      title: 'Descrizione',
      dataIndex: 'product_description',
      key: 'product_description',
    },
    {
      title: 'Quantità Mancante',
      dataIndex: 'shortage_quantity',
      key: 'shortage_quantity',
      render: (qty: number) => <Tag color="orange">{qty}</Tag>,
    },
    {
      title: 'Quantità Ordinata',
      dataIndex: 'quantity_ordered',
      key: 'quantity_ordered',
      render: (qty: number) => <Tag color="green">{qty}</Tag>,
    },
    {
      title: 'Costo Unitario',
      dataIndex: 'cost',
      key: 'cost',
      render: (cost: number) => `€ ${formatCurrency(cost)}`,
    },
    {
      title: 'Totale',
      key: 'total',
      render: (_: any, record: any) => `€ ${formatCurrency(record.quantity_ordered * record.cost)}`,
    },
  ];

  const renderConfirmStep = () => (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <ExclamationCircleOutlined style={{ fontSize: 48, color: '#faad14', marginBottom: 16 }} />
        <Title level={4}>Conferma Conversione</Title>
        <Text>
          Stai per convertire l'ordine cliente <strong>{customerOrderNumber}</strong> in ordini fornitori.
        </Text>
      </div>
      
      <div style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: 16, marginBottom: 16 }}>
        <Title level={5} style={{ color: '#52c41a', marginBottom: 8 }}>
          <CheckCircleOutlined /> Cosa succederà:
        </Title>
        <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
          <li>Il sistema controllerà la disponibilità di ogni prodotto nei magazzini</li>
          <li>Per i prodotti con disponibilità insufficiente, verrà creato un ordine al fornitore primario</li>
          <li>I prodotti dello stesso fornitore verranno raggruppati in un unico ordine</li>
          <li>Verranno rispettate le quantità minime di ordine per ogni fornitore</li>
        </ul>
      </div>
    </div>
  );

  const renderProcessingStep = () => (
    <div style={{ textAlign: 'center', padding: '40px 0' }}>
      <ShoppingCartOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
      <Title level={4}>Conversione in corso...</Title>
      <Text>Sto analizzando le disponibilità e creando gli ordini fornitori</Text>
    </div>
  );

  const renderResultStep = () => {
    if (!result) return null;

    return (
      <div>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
          <Title level={4}>Conversione Completata!</Title>
          <Text>{result.message}</Text>
        </div>

        {result.supplier_orders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 20, backgroundColor: '#f0f2f5', borderRadius: 6 }}>
            <Text>Tutti i prodotti sono già disponibili in magazzino. Non è necessario creare ordini fornitori.</Text>
          </div>
        ) : (
          <div>
            <Title level={5}>Ordini Fornitori Creati:</Title>
            {result.supplier_orders.map((supplierOrder, index) => (
              <div key={index} style={{ marginBottom: 24, border: '1px solid #d9d9d9', borderRadius: 6, padding: 16 }}>
                <div style={{ marginBottom: 16 }}>
                  <Space direction="vertical" size="small">
                    <Text strong style={{ fontSize: 16 }}>{supplierOrder.supplier_name}</Text>
                    <Space>
                      <Text>Numero Ordine: <Text code>{supplierOrder.order_number}</Text></Text>
                      <Text>Consegna Stimata: <Text strong>{new Date(supplierOrder.estimated_delivery).toLocaleDateString()}</Text></Text>
                    </Space>
                    <Text>Prodotti: <Tag color="blue">{supplierOrder.products_count}</Tag></Text>
                  </Space>
                </div>
                
                <Table
                  columns={productColumns}
                  dataSource={supplierOrder.products}
                  rowKey="product_code"
                  size="small"
                  pagination={false}
                  summary={(pageData) => {
                    const total = pageData.reduce((sum, record) => sum + (record.quantity_ordered * record.cost), 0);
                    return (
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={5}>
                          <Text strong>Totale Ordine</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1}>
                          <Text strong>€ {formatCurrency(total)}</Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    );
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Modal
        title="Trasforma in Ordine Fornitore"
        open={visible}
        onCancel={handleClose}
        width={step === 'result' ? 1000 : 600}
        footer={
          step === 'confirm' ? [
            <Button key="cancel" onClick={handleClose}>
              Annulla
            </Button>,
            <Button key="convert" type="primary" onClick={handleConvert} loading={loading}>
              Conferma Conversione
            </Button>,
          ] : step === 'processing' ? null : step === 'supplier-association' ? null : [
            <Button key="close" type="primary" onClick={handleClose}>
              Chiudi
            </Button>,
          ]
        }
      >
        {step === 'confirm' && renderConfirmStep()}
        {step === 'processing' && renderProcessingStep()}
        {step === 'result' && renderResultStep()}
      </Modal>

      {/* Modal per associazione fornitori */}
      <SupplierAssociationModal
        visible={showSupplierModal}
        onClose={handleSupplierModalClose}
        customerOrderId={customerOrderId}
        customerOrderNumber={customerOrderNumber}
        productsWithoutSuppliers={result?.products_without_suppliers || []}
        onAssociationsComplete={handleAssociationsComplete}
      />
    </>
  );
};

export default ConvertToSupplierOrdersModal;
